import {
  JAVASCRIPT_COURSE_SPLIT_LESSON_ALIASES,
  type JavascriptCourseSplitLessonAlias,
} from "./javascript-course-split.generated";
import {
  TYPESCRIPT_REACT_NEXTJS_LESSON_ALIASES,
  type TypescriptReactNextjsCourseSplitLessonAlias,
} from "./typescript-react-nextjs-course-split.generated";
import {
  HTML_CSS_LESSON_ALIASES,
  type HtmlCssCourseSplitLessonAlias,
} from "./html-css-course-split.generated";
import {
  SQL_COURSE_SPLIT_LESSON_ALIASES,
  type SqlCourseSplitLessonAlias,
} from "./sql-course-split.generated";
import {
  COURSE_SPLIT_V2_LESSON_ALIASES,
  type CourseSplitV2StaticLessonAlias,
} from "./curriculum-course-split-v2.generated";
import {
  LINUC_SERVER_CS_LESSON_ALIASES,
  type LinucServerCsCourseSplitLessonAlias,
} from "./linuc-server-cs-course-split.generated";

export type CurriculumCourseSplitLessonAlias =
  | JavascriptCourseSplitLessonAlias
  | TypescriptReactNextjsCourseSplitLessonAlias
  | HtmlCssCourseSplitLessonAlias
  | SqlCourseSplitLessonAlias
  | CourseSplitV2StaticLessonAlias
  | LinucServerCsCourseSplitLessonAlias;

/**
 * One cross-migration static-key contract. Canonical objects remain enumerable
 * exactly once under old keys; each post-transaction key resolves to that same
 * object or CONTENT record.
 */
export const CURRICULUM_COURSE_SPLIT_LESSON_ALIASES = Object.freeze([
  ...JAVASCRIPT_COURSE_SPLIT_LESSON_ALIASES,
  ...TYPESCRIPT_REACT_NEXTJS_LESSON_ALIASES,
  ...HTML_CSS_LESSON_ALIASES,
  ...SQL_COURSE_SPLIT_LESSON_ALIASES,
  ...COURSE_SPLIT_V2_LESSON_ALIASES,
  ...LINUC_SERVER_CS_LESSON_ALIASES,
]) satisfies readonly CurriculumCourseSplitLessonAlias[];

export const CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY: Readonly<
  Record<string, string>
> = Object.freeze(
  Object.fromEntries(
    CURRICULUM_COURSE_SPLIT_LESSON_ALIASES.map(({ oldKey, newKey }) => [
      oldKey,
      newKey,
    ]),
  ),
);
export const CURRICULUM_COURSE_SPLIT_NEW_TO_OLD_LESSON_KEY: Readonly<
  Record<string, string>
> = Object.freeze(
  Object.fromEntries(
    CURRICULUM_COURSE_SPLIT_LESSON_ALIASES.map(({ oldKey, newKey }) => [
      newKey,
      oldKey,
    ]),
  ),
);

if (
  Object.keys(CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY).length !==
    CURRICULUM_COURSE_SPLIT_LESSON_ALIASES.length ||
  Object.keys(CURRICULUM_COURSE_SPLIT_NEW_TO_OLD_LESSON_KEY).length !==
    CURRICULUM_COURSE_SPLIT_LESSON_ALIASES.length
) {
  throw new Error("duplicate curriculum split static lesson key");
}
for (const newKey of Object.keys(
  CURRICULUM_COURSE_SPLIT_NEW_TO_OLD_LESSON_KEY,
)) {
  if (CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY[newKey]) {
    throw new Error(`chained curriculum split static lesson key: ${newKey}`);
  }
}

/** Canonical pre-transaction identity for every static lesson-key consumer. */
export function canonicalCurriculumLessonKey(key: string): string {
  return CURRICULUM_COURSE_SPLIT_NEW_TO_OLD_LESSON_KEY[key] ?? key;
}

/** Both public keys for moved lessons; one key for unaffected lessons. */
export function curriculumLessonKeyAliases(key: string): readonly string[] {
  const canonical = canonicalCurriculumLessonKey(key);
  const next = CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY[canonical];
  return next ? [canonical, next] : [canonical];
}
